import { useState } from "react";
import { FaTrash } from "react-icons/fa";
import MsTable from "../../../../components/Widgets/Table";
import PageHeader from "components/PageHeader";
import { colType } from "../../../../Types";
import { MomentOccasion } from "../../../../api/moments";
import { extractApiErrorMessage } from "../../../../utils/util";
import DSAlert from "../../../system-design/DSAlert";
import DSDialog from "../../../system-design/DSDialog";
import DSDialogActions from "../../../system-design/DSDialogActions";
import DSDialogContent from "../../../system-design/DSDialogContent";
import DSDialogTitle from "../../../system-design/DSDialogTitle";
import DSErrorButton from "../../../system-design/DSErrorButton";
import DSPrimaryButton from "../../../system-design/DSPrimaryButton";
import DSSecondaryButton from "../../../system-design/DSSecondaryButton";
import DSTypography from "../../../system-design/DSTypography";
import useDeleteMomentOccasionMutation from "../hooks/useDeleteMomentOccasionMutation";
import useMomentOccasionsQuery from "../hooks/useMomentOccasionsQuery";
import MomentCoverPhoto from "./MomentCoverPhoto";
import MomentKlydoSelection from "./MomentKlydoSelection";
import MomentOccasionModal from "./MomentOccasionModal";

const tableColumns: Array<colType> = [
  { label: "order", field: "order", sort: true },
  { label: "label", field: "label", sort: true },
  { label: "key", field: "key", sort: true },
  { label: "logo", field: "logo", sort: false },
  { label: "klydos", field: "numKlydos", sort: true },
  { label: "active", field: "active", sort: true },
  { size: 32, noHeadline: true, sort: false, label: "", field: "delete" },
];

// "Klydo App" > "Moments": the whitelist of occasions users can pick in the
// app's "Add a moment", and the klydos assigned to each.
const MomentOccasions = () => {
  const { data: occasions, error } = useMomentOccasionsQuery();
  const {
    mutate: deleteOccasion,
    isPending: isDeleting,
    error: deleteError,
  } = useDeleteMomentOccasionMutation();
  const [showAdd, setShowAdd] = useState(false);
  const [keyToDelete, setKeyToDelete] = useState("");

  const rowBuild = (occasion: MomentOccasion & { numKlydos: number }) => ({
    order: occasion.order,
    label: occasion.label,
    key: occasion.key,
    logo: occasion.logoUrl ? (
      <img
        width={40}
        src={occasion.logoUrl}
        alt={occasion.label}
        style={{ backgroundColor: "#333", borderRadius: 8, padding: 4 }}
      />
    ) : (
      <DSTypography variant="caption">No logo</DSTypography>
    ),
    numKlydos: occasion.numKlydos,
    active: (
      <DSTypography
        sx={{ color: occasion.active ? "green" : "red", fontWeight: "bold" }}
      >
        {occasion.active ? "Active" : "Inactive"}
      </DSTypography>
    ),
    delete: (
      <FaTrash
        style={{ margin: "8px", cursor: "pointer" }}
        title="Delete occasion"
        onClick={(e) => {
          e.stopPropagation();
          setKeyToDelete(occasion.key);
        }}
      />
    ),
  });

  return (
    <>
      <PageHeader
        overline="Klydo App"
        title="Moments"
        subtitle="The occasions users can pick when they add a moment in the app, the klydos assigned to each, and how a klydo is chosen."
        actions={
          <DSPrimaryButton onClick={() => setShowAdd(true)}>
            Add occasion
          </DSPrimaryButton>
        }
      />
      <MomentKlydoSelection />
      <MomentCoverPhoto />
      {error && (
        <DSAlert severity="error" sx={{ mb: 2 }}>
          {extractApiErrorMessage(error, "Failed to load the occasions")}
        </DSAlert>
      )}
      {occasions && (
        <MsTable
          id="MomentOccasions"
          data={occasions.map((occasion) => ({
            ...occasion,
            numKlydos: occasion.klydoIds.length,
          }))}
          cols={tableColumns}
          rowBuild={rowBuild}
          title="Occasions"
          search={(row: MomentOccasion, val: string) =>
            `${row.label} ${row.key}`.toLowerCase().includes(val.toLowerCase())
          }
          modal={MomentOccasionModal}
        />
      )}
      {showAdd && (
        <DSDialog
          open
          fullWidth
          maxWidth="md"
          onClose={() => setShowAdd(false)}
        >
          <MomentOccasionModal setShowModal={setShowAdd} />
        </DSDialog>
      )}
      {keyToDelete && (
        <DSDialog open onClose={() => setKeyToDelete("")}>
          <DSDialogTitle>Delete occasion</DSDialogTitle>
          <DSDialogContent>
            {deleteError && (
              <DSAlert severity="error" sx={{ mb: 2 }}>
                {extractApiErrorMessage(deleteError, "Failed to delete")}
              </DSAlert>
            )}
            Delete &quot;{keyToDelete}&quot;? Moments users already created with
            it keep working, but it will no longer be offered in the app.
          </DSDialogContent>
          <DSDialogActions>
            <DSSecondaryButton onClick={() => setKeyToDelete("")}>
              Cancel
            </DSSecondaryButton>
            <DSErrorButton
              disabled={isDeleting}
              onClick={() =>
                deleteOccasion(keyToDelete, {
                  onSuccess: () => setKeyToDelete(""),
                })
              }
            >
              Delete
            </DSErrorButton>
          </DSDialogActions>
        </DSDialog>
      )}
    </>
  );
};

export default MomentOccasions;
